import React, { useState, useEffect, useMemo } from "react";
import ClampedText from "../../components/common/ClampedText.jsx";
import {
  Search,
  Filter,
  Eye,
  AlertTriangle,
  Calendar,
  Building2,
  User,
  FileText,
  ChevronDown,
  X,
  RefreshCw,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  getPolicyWriterApplications,
  getInsuranceCompaniesForFilter,
  getHospitalsForFilter,
  getPolicyApplicationsOnHold,
} from "../../services/policyWriterService";
import PolicyApplicationDetails from "./PolicyApplicationDetails";
import { useNavigate } from "react-router-dom";

const ApplicationsList = ({ onApplicationSelect, initialSelectedApplication, initialActiveTab }) => {
  const [applications, setApplications] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(10);
  const navigate = useNavigate()

  // Filters
  const [filters, setFilters] = useState({
    searchTerm: "",
    status: "",
    priority: "",
    emergency: "",
    treatmentType: "",
    insuranceCompany: "",
    hospitalName: "",
    dateFrom: "",
    dateTo: "",
    minCoverage: "",
    maxCoverage: "",
  });

  // Bucket tabs for policy pending applications
  const [bucket, setBucket] = useState('all'); // all | new | diagnosis_approved | query_reply_received | kyp_challenge

  // Sort
  const [sortField, setSortField] = useState("submittedAt");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    loadApplications();
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortField, sortDirection]);

  // Separate effect for non-search filters to avoid unnecessary API calls
  useEffect(() => {
    const { searchTerm, ...otherFilters } = filters;
    const hasNonSearchFilters = Object.values(otherFilters).some(value => value !== "");
    
    if (hasNonSearchFilters) {
      loadApplications();
    }
  }, [filters.status, filters.priority, filters.emergency, filters.treatmentType, filters.insuranceCompany, filters.hospitalName, filters.dateFrom, filters.dateTo, filters.minCoverage, filters.maxCoverage]);

  // Handle initial selected application from props
  useEffect(() => {
    if (initialSelectedApplication) {
      setSelectedApplication(initialSelectedApplication);
    }
  }, [initialSelectedApplication]);

  // Reset page when search term or bucket changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.searchTerm, bucket]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      // Fetch base pending apps and on-hold (dispute_pending) apps, then merge & enrich
      const [pendingRes, onHoldRes] = await Promise.all([
        getPolicyWriterApplications(filters, currentPage, pageSize),
        getPolicyApplicationsOnHold({}, 1, 500),
      ]);

      let merged = [];
      if (pendingRes?.success) merged = [...pendingRes.applications];
      if (onHoldRes?.success) {
        // Merge by applicationId, prefer pendingRes version when duplicate
        const seen = new Set(merged.map(a => a.applicationId));
        onHoldRes.applications.forEach(app => {
          if (!seen.has(app.applicationId)) {
            merged.push(app);
          }
        });
      }

      // Enrich with dispute reply flags (policy-specific)
      try {
        const { getApplicationDisputes } = await import("../../services/disputeService");
        const enriched = await Promise.all(merged.map(async (app) => {
          try {
            const res = await getApplicationDisputes(app.applicationId);
            let policyHasHospitalReply = false;
            let policyHasAcceptedReply = false;
            let policyHasActiveDispute = false;
            if (res?.success && Array.isArray(res.disputes)) {
              for (const d of res.disputes) {
                const status = String(d?.status || '').toLowerCase();
                const raisedDept = String((d?.raisedBy?.department || '')).toLowerCase();
                const isPolicy = raisedDept === 'policy';
                if (!isPolicy) continue;
                const hasResolverResponse = !!d?.resolverResponse;
                const hasHospitalComm = Array.isArray(d?.communications) && d.communications.some((c) => String(c?.fromDepartment || '').toLowerCase() === 'hospital');
                const isActiveReply = ['raised','acknowledged','under_review','resolved'].includes(status) && status !== 'accepted';
                if ((hasResolverResponse || hasHospitalComm) && isActiveReply) {
                  policyHasHospitalReply = true;
                }
                const flagAccepted = d?.accepted === true;
                const commAccepted = Array.isArray(d?.communications) && d.communications.some((c) => String(c?.type || '').toLowerCase() === 'accepted');
                if (flagAccepted || status === 'accepted' || commAccepted) {
                  policyHasAcceptedReply = true;
                }
                if (['raised','acknowledged','under_review'].includes(status)) policyHasActiveDispute = true;
              }
            }
            return { ...app, queryReplies: { ...(app.queryReplies || {}), policyHasHospitalReply, policyHasAcceptedReply, policyHasActiveDispute } };
          } catch (_) {
            return app;
          }
        }));

        merged = enriched;
      } catch (_) {
        // If enrichment fails, continue with base data
      }

      // Update state with merged data; recompute pagination client-side for merged list
      setAllApplications(merged);
      const totalAll = merged.length;
      const startIndex = (currentPage - 1) * pageSize;
      const paginated = merged.slice(startIndex, startIndex + pageSize);
      setApplications(paginated);
      setTotal(totalAll);
      setTotalPages(Math.ceil(totalAll / pageSize));
    } catch (error) {
      console.error("Error loading applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [companiesResult, hospitalsResult] = await Promise.all([
        getInsuranceCompaniesForFilter(),
        getHospitalsForFilter(),
      ]);

      if (companiesResult.success) {
        setInsuranceCompanies(companiesResult.companies);
      }
      if (hospitalsResult.success) {
        setHospitals(hospitalsResult.hospitals);
      }
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadApplications();
    setRefreshing(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    // Only reset page for non-search filters
    if (key !== 'searchTerm') {
      setCurrentPage(1);
    }
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: "",
      status: "",
      priority: "",
      emergency: "",
      treatmentType: "",
      insuranceCompany: "",
      hospitalName: "",
      dateFrom: "",
      dateTo: "",
      minCoverage: "",
      maxCoverage: "",
    });
    setCurrentPage(1);
  };

  const matchesBucket = (app) => {
    const isOnHold = app.status === 'dispute_pending';
    const pvStatus = app.workflow?.policyVerification?.status;
    const inPolicyPhaseOrUnset = !pvStatus || pvStatus === 'pending';
    const earlyPipeline = (
      app.status === 'submitted' ||
      app.status === 'under_medical_review' ||
      app.status === 'medical_review_pending' ||
      app.status === 'under_review'
    );
    const isPolicyCandidate = inPolicyPhaseOrUnset && (
      earlyPipeline ||
          app.status === 'policy_review_pending' ||
      app.status === 'medical_approved' ||
      app.status === 'approved_for_process'
    );
    const mv = app.workflow?.medicalVerification || {};
    const diagnosisApproved = (
      mv?.status === 'approved' ||
      mv?.approvedForProcess === true ||
      app?.status === 'approved_for_process' ||
      app?.status === 'medical_approved'
    );
    const kypChallenge = mv?.kypHospitalAcknowledgement?.status === 'challenged' || mv?.kypReassignment;
        const pv = app.workflow?.policyVerification;
    const replyFlag = pv?.queryReplyReceived === true; // policy-scope flag
    const enrichedPolicyReply = app?.queryReplies?.policyHasHospitalReply === true || app?.queryReplies?.policyHasAcceptedReply === true;
    const policyReply = replyFlag || enrichedPolicyReply || (isOnHold && app?.queryReplies?.policyHasActiveDispute === true);

    switch (bucket) {
      case 'new': {
        // Exclusive catch-all for policy candidates: exclude other buckets and on-hold
        return Boolean(
          isPolicyCandidate &&
          !isOnHold &&
          !diagnosisApproved &&
          !policyReply &&
          !kypChallenge
        );
      }
      case 'diagnosis_approved': {
        return Boolean(diagnosisApproved && (inPolicyPhaseOrUnset) && !isOnHold);
      }
      case 'query_reply_received': {
        return Boolean(policyReply);
      }
      case 'kyp_challenge': {
        return Boolean(kypChallenge);
      }
      default:
        return true;
    }
  };

  // Memoized client-side search function for better performance
  // Note: matchesBucket closure over 'bucket' state, so we don't need to add it to deps
  const filteredApplications = useMemo(() => {
    let filtered = allApplications;

    // Apply search term filter
    if (filters.searchTerm && filters.searchTerm.trim() !== "") {
      const searchTerm = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(app =>
        app.applicationId?.toLowerCase().includes(searchTerm) ||
        app.patient?.name?.toLowerCase().includes(searchTerm) ||
        app.policy?.number?.toLowerCase().includes(searchTerm) ||
        app.hospitalName?.toLowerCase().includes(searchTerm) ||
        app.policy?.insuranceCompany?.toLowerCase().includes(searchTerm) ||
        app.medical?.diagnosis?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply bucket filter
    filtered = filtered.filter(matchesBucket);

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allApplications, filters.searchTerm, bucket]);

  // Client-side search function (kept for backward compatibility)
  const getFilteredApplications = () => filteredApplications;

  const bucketCounts = useMemo(() => {
    const counts = { all: 0, new: 0, diagnosis_approved: 0, query_reply_received: 0, kyp_challenge: 0 };
    const uncategorized = [];
    
    // Use filtered applications for bucket counts (excluding search term)
    const applicationsForBuckets = filters.searchTerm && filters.searchTerm.trim() !== "" 
      ? allApplications.filter(app => {
          const searchTerm = filters.searchTerm.toLowerCase();
          return app.applicationId?.toLowerCase().includes(searchTerm) ||
                 app.patient?.name?.toLowerCase().includes(searchTerm) ||
                 app.policy?.number?.toLowerCase().includes(searchTerm) ||
                 app.hospitalName?.toLowerCase().includes(searchTerm) ||
                 app.policy?.insuranceCompany?.toLowerCase().includes(searchTerm) ||
                 app.medical?.diagnosis?.toLowerCase().includes(searchTerm);
        })
      : allApplications;
    
    applicationsForBuckets.forEach((app) => {
      counts.all += 1;

      const isOnHold = app.status === 'dispute_pending';
      const pvStatus = app.workflow?.policyVerification?.status;
      const inPolicyPhaseOrUnset = !pvStatus || pvStatus === 'pending';
      const earlyPipeline = (
        app.status === 'submitted' ||
        app.status === 'under_medical_review' ||
        app.status === 'medical_review_pending' ||
        app.status === 'under_review'
      );
      const isPolicyCandidate = inPolicyPhaseOrUnset && (
        earlyPipeline ||
        app.status === 'policy_review_pending' ||
        app.status === 'medical_approved' ||
        app.status === 'approved_for_process'
      );
      const mv = app.workflow?.medicalVerification || {};
      const diagnosisApproved = (
        mv?.status === 'approved' ||
        mv?.approvedForProcess === true ||
        app?.status === 'approved_for_process' ||
        app?.status === 'medical_approved'
      );
      const kypChallenge = mv?.kypHospitalAcknowledgement?.status === 'challenged' || mv?.kypReassignment;
      const pv = app.workflow?.policyVerification;
      const replyFlag = pv?.queryReplyReceived === true;
      const enrichedPolicyReply = app?.queryReplies?.policyHasHospitalReply === true || app?.queryReplies?.policyHasAcceptedReply === true;
      const policyReply = replyFlag || enrichedPolicyReply || (isOnHold && app?.queryReplies?.policyHasActiveDispute === true);

      let categorized = false;

      // Use the same logic as matchesBucket for consistency
      if (diagnosisApproved && inPolicyPhaseOrUnset && !isOnHold) {
        counts.diagnosis_approved += 1;
        categorized = true;
      } else if (policyReply) {
        counts.query_reply_received += 1;
        categorized = true;
      } else if (kypChallenge) {
        counts.kyp_challenge += 1;
        categorized = true;
      } else if (isPolicyCandidate && !isOnHold && !diagnosisApproved && !policyReply && !kypChallenge) {
        counts.new += 1;
        categorized = true;
      }

      // If not categorized, add to new bucket as fallback
      if (!categorized) {
        counts.new += 1;
        uncategorized.push({
          applicationId: app.applicationId,
          status: app.status,
          pvStatus: pvStatus,
          diagnosisApproved: diagnosisApproved,
          policyReply: policyReply,
          kypChallenge: kypChallenge,
          isOnHold: isOnHold
        });
      }
    });

    // Debug: log uncategorized items
    if (uncategorized.length > 0) {
      console.log('Uncategorized applications in policy list:', uncategorized);
    }

    return counts;
  }, [allApplications, filters.searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const getPriorityColor = (priority, emergency) => {
    if (emergency) return "bg-red-100 text-red-800 border-red-200";
    switch (priority) {
      case "urgent":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "high":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "medical_approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "policy_review_pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const renderFilters = () => (
    <div
      className={`transition-all duration-300 overflow-hidden ${
        showFilters ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
      }`}
    >
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Status</option>
              <option value="medical_approved">Medical Approved</option>
              <option value="policy_review_pending">
                Policy Review Pending
              </option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange("priority", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Priorities</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          {/* Emergency Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Emergency
            </label>
            <select
              value={filters.emergency}
              onChange={(e) => handleFilterChange("emergency", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Cases</option>
              <option value="true">Emergency Only</option>
              <option value="false">Non-Emergency</option>
            </select>
          </div>

          {/* Treatment Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Treatment Type
            </label>
            <select
              value={filters.treatmentType}
              onChange={(e) =>
                handleFilterChange("treatmentType", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Types</option>
              <option value="medical">Medical</option>
              <option value="surgical">Surgical</option>
            </select>
          </div>

          {/* Insurance Company Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Company
            </label>
            <select
              value={filters.insuranceCompany}
              onChange={(e) =>
                handleFilterChange("insuranceCompany", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Companies</option>
              {insuranceCompanies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>

          {/* Hospital Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hospital
            </label>
            <select
              value={filters.hospitalName}
              onChange={(e) =>
                handleFilterChange("hospitalName", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Hospitals</option>
              {hospitals.map((hospital) => (
                <option key={hospital.id} value={hospital.name}>
                  {hospital.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date From
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date To
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        {/* Coverage Amount Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Coverage Amount
            </label>
            <input
              type="number"
              placeholder="Enter minimum amount"
              value={filters.minCoverage}
              onChange={(e) =>
                handleFilterChange("minCoverage", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Coverage Amount
            </label>
            <input
              type="number"
              placeholder="Enter maximum amount"
              value={filters.maxCoverage}
              onChange={(e) =>
                handleFilterChange("maxCoverage", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={clearFilters}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <X className="h-4 w-4" />
            <span>Clear Filters</span>
          </button>

          <div className="text-sm text-gray-600">
            {total} application(s) found
          </div>
        </div>
      </div>
    </div>
  );

  const renderTableHeader = () => (
    <thead className="bg-gray-50">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          <button
            onClick={() => handleSort("applicationId")}
            className="flex items-center space-x-1 hover:text-gray-700"
          >
            <span>Application</span>
            <ArrowUpDown className="h-3 w-3" />
          </button>
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          <button
            onClick={() => handleSort("patient.name")}
            className="flex items-center space-x-1 hover:text-gray-700"
          >
            <span>Patient</span>
            <ArrowUpDown className="h-3 w-3" />
          </button>
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Hospital
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Policy Details
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          <button
            onClick={() => handleSort("medical.estimatedCost")}
            className="flex items-center space-x-1 hover:text-gray-700"
          >
            <span>Amount</span>
            <ArrowUpDown className="h-3 w-3" />
          </button>
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          <button
            onClick={() => handleSort("submittedAt")}
            className="flex items-center space-x-1 hover:text-gray-700"
          >
            <span>Submitted</span>
            <ArrowUpDown className="h-3 w-3" />
          </button>
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Status
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Actions
        </th>
      </tr>
    </thead>
  );

  const handleRowClick = (e, application) => {
    // Support Ctrl+Click or Cmd+Click to open in new tab
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      const url = `${window.location.origin}/policy/details/${application?.applicationId}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(`/policy/details/${application?.applicationId}`);
  };

  const renderTableRow = (application) => (
    <tr
      key={application.id}
      className="bg-white hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={(e) => handleRowClick(e, application)}
    >
      <td className="px-6 py-4 whitespace-normal">
        <div className="flex items-center space-x-2">
          <div>
            <ClampedText className="text-sm font-medium text-gray-900" value={application.applicationId} />
            <div className="flex items-center space-x-2 mt-1">
              {application.emergency && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Emergency
                </span>
              )}
              {application.priority && application.priority !== "normal" && (
                <span
                  className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                    application.priority,
                    application.emergency
                  )}`}
                >
                  {application.priority.charAt(0).toUpperCase() +
                    application.priority.slice(1)}
                </span>
              )}
            </div>
          </div>
        </div>
      </td>

      <td className="px-6 py-4 whitespace-normal">
        <div className="flex items-center">
          <div className="bg-gray-100 rounded-full p-2 mr-3">
            <User className="h-4 w-4 text-gray-600" />
          </div>
          <div>
            <ClampedText className="text-sm font-medium text-gray-900" value={application.patient?.name} />
            <ClampedText className="text-sm text-gray-500" value={`${application.patient?.age} years, ${application.patient?.gender}`} />
          </div>
        </div>
      </td>

      <td className="px-6 py-4 whitespace-normal">
        <div className="flex items-center">
          <div className="bg-blue-100 rounded-full p-2 mr-3">
            <Building2 className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <ClampedText className="text-sm font-medium text-gray-900" value={application.hospitalName} />
            <ClampedText className="text-sm text-gray-500" value={`${application.medical?.treatmentType?.charAt(0).toUpperCase()}${application.medical?.treatmentType?.slice(1)} Treatment`} />
          </div>
        </div>
      </td>

      <td className="px-6 py-4 whitespace-normal">
        <div>
          <ClampedText className="text-sm font-medium text-gray-900" value={application.policy?.insuranceCompany} />
          <ClampedText className="text-sm text-gray-500 font-mono" value={application.policy?.number} />
          <ClampedText className="text-sm text-gray-500" value={`Type: ${application.policy?.type?.charAt(0).toUpperCase()}${application.policy?.type?.slice(1)}`}/>
        </div>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900">
            {formatAmount(application.policy?.coverageAmount)}
          </div>
          <div className="text-sm text-gray-500">
            Claim: {formatAmount(application.medical?.estimatedCost)}
          </div>
          <div className="text-xs text-gray-400">
            Ratio:{" "}
            {application.policy?.coverageAmount > 0
              ? Math.round(
                  (application.medical?.estimatedCost /
                    application.policy?.coverageAmount) *
                    100
                )
              : 0}
            %
          </div>
        </div>
      </td>

      <td className="px-6 py-4 whitespace-normal">
        <div className="flex items-center">
          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
          <div>
            <ClampedText className="text-sm text-gray-900" value={formatDate(application.submittedAt)} />
            <ClampedText className="text-xs text-gray-500" value={application.submittedAt ? (() => { const submittedDate = application.submittedAt.toDate ? application.submittedAt.toDate() : new Date(application.submittedAt); const daysDiff = Math.floor((new Date() - submittedDate) / (1000 * 60 * 60 * 24)); return `${daysDiff} day${daysDiff !== 1 ? "s" : ""} ago`; })() : ""} />
          </div>
        </div>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(
            application.status
          )}`}
        >
          {application.status
            ?.replace("_", " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}
        </span>
      </td>

      <td className="px-6 py-4 whitespace-normal text-right text-sm font-medium">
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click from firing
            navigate(`/policy/details/${application?.applicationId}`)
          }}
          className="flex items-center space-x-1 text-teal-600 hover:text-teal-800 transition-colors"
        >
          <Eye className="h-4 w-4" />
          <span>Review</span>
        </button>
      </td>
    </tr>
  );

  const renderPagination = () => {
    const filteredApps = getFilteredApplications();
    const filteredTotal = filteredApps.length;
    const filteredTotalPages = Math.ceil(filteredTotal / pageSize);
    
    return (
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, filteredTotalPages))
            }
            disabled={currentPage === filteredTotalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>

        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {filteredTotal > 0 ? (currentPage - 1) * pageSize + 1 : 0}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(currentPage * pageSize, filteredTotal)}
              </span>{" "}
              of <span className="font-medium">{filteredTotal}</span> results
            </p>
          </div>

          <div>
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              {[...Array(Math.min(filteredTotalPages, 5))].map((_, i) => {
                const pageNum = i + 1;
                const isActive = pageNum === currentPage;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      isActive
                        ? "z-10 bg-teal-50 border-teal-500 text-teal-600"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, filteredTotalPages))
                }
                disabled={currentPage === filteredTotalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  const handleBackToList = () => {
    setSelectedApplication(null);
    if (onApplicationSelect) {
      onApplicationSelect(null);
    }
    loadApplications(); // Refresh the list
  };

  // If an application is selected, show the details component
  if (selectedApplication) {
    return (
      <PolicyApplicationDetails
        applicationId={selectedApplication.applicationId}
        initialActiveTab={initialActiveTab || 'details'}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pending Applications</h1>
          <p className="text-gray-600">
            Review and verify insurance policies for medical claims
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Buckets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'new', label: 'New Application' },
              { key: 'diagnosis_approved', label: 'Diagnosis Approved' },
              { key: 'query_reply_received', label: 'Query Reply Received' },
              { key: 'kyp_challenge', label: 'KYP Challenge' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setBucket(tab.key)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  bucket === tab.key
                    ? 'bg-teal-50 text-teal-700 border-teal-200'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span>{tab.label}</span>
                <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                  {bucketCounts[tab.key] ?? 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-4 mb-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by application ID, patient name, policy number, or hospital..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
              showFilters
                ? "bg-teal-50 border-teal-200 text-teal-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>

        {renderFilters()}
      </div>

      {/* Applications Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading applications...</p>
            </div>
          </div>
        ) : (() => {
          const filteredApps = getFilteredApplications();
          const paginatedApps = filteredApps.slice(
            (currentPage - 1) * pageSize,
            currentPage * pageSize
          );
          
          return filteredApps.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {renderTableHeader()}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedApps.map(renderTableRow)}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Applications Found
              </h3>
              <p className="text-gray-600 mb-4">
                {Object.values(filters).some((f) => f !== "") || bucket !== 'all'
                  ? "No applications match your current filters."
                  : "No applications are currently pending policy review."}
              </p>
              {Object.values(filters).some((f) => f !== "") && (
                <button
                  onClick={clearFilters}
                  className="text-teal-600 hover:text-teal-700 font-medium"
                >
                  Clear filters to show all applications
                </button>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ApplicationsList;
